// apps/token-seek/src/components/Home/StockMarketSwitch.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { usePathname } from "next/navigation";
import Translations from "@translate/components/Home/StockMarketSwitch";
import { I18nUtil } from "@utils/i18n";
import NextImage from "@/src/components/NextImage";
import { useIsPc } from "@/src/hooks/useIsPc";
import {
  type StockType,
  getStockTypeFromPathname,
  getStockTypeRoute,
} from "@/src/utils/stockType";
import { useShellRouter } from "@/src/hooks";

const I18n = I18nUtil.createScoped(Translations);

interface StockMarketSwitchProps {
  className?: string;
  /** 弹层相对触发按钮的对齐方式：左对齐或右对齐 */
  align?: "left" | "right";
}

interface StockTypeOption {
  value: StockType;
  label: string;
  iconSrc: string;
}

/**
 * @description 获取股票市场类型选项配置
 * @returns 返回选项数组，每次调用都会重新获取翻译，确保多语言正确
 */
const getStockTypeOptions = (): StockTypeOption[] => {
  return [
    {
      value: "US",
      label: I18n.t("US Market"),
      iconSrc: "/assets/images/homepage/US.svg",
    },
    {
      value: "HK",
      label: I18n.t("HK Market"),
      iconSrc: "/assets/images/homepage/HK.svg",
    },
  ];
};

export const StockMarketSwitch = ({
  className,
  align = "right",
}: StockMarketSwitchProps) => {
  const [isPc] = useIsPc();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useShellRouter();

  // 从路由获取当前 stockType
  const stockType = getStockTypeFromPathname(pathname);

  const stockTypeOptions = getStockTypeOptions();

  const currentOption =
    stockTypeOptions.find((option) => option.value === stockType) ||
    stockTypeOptions[0]; // 默认 US Market

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleStockTypeChange = (newStockType: StockType) => {
    setIsOpen(false);
    if (newStockType !== stockType) {
      // 通过路由导航切换市场类型
      const newPath = getStockTypeRoute(pathname, newStockType);
      router.push(newPath);
    }
  };

  return (
    // 外层包裹：只负责接收 className 做整体布局（如 w-full、margin 等）
    <div className={className}>
      {/* 内层相对定位容器：严格包裹按钮，作为弹层的定位参考 */}
      <div className="relative inline-flex" ref={containerRef}>
        {isOpen && (
          <div
            className={cn(
              "absolute md:top-[46px] top-[32px] z-10",
              // 根据 align 决定弹层相对按钮左对齐还是右对齐
              align === "left" ? "left-0" : "right-0",
              // 弹层自己的尺寸：min-w 保证最小宽度，w-max 让内容自动撑开
              "md:min-w-[135px] min-w-[113px] w-max rounded-2 md:px-[7px] px-2 md:py-[7px] py-1",
              "border border-border_white_transparent10",
              "bg-background_solid_neutral_tertiary cursor-pointer",
            )}
          >
            {stockTypeOptions.map((option) => (
              <div
                data-openreplay-label="Home · StockMarketSwitch · div"
                key={option.value}
                className={cn(
                  "px-2 py-[10px] body_s text-content_primary/60",
                  "hover:bg-background_white_overlay5",
                  "rounded-[6px] flex items-center gap-2 cursor-pointer whitespace-nowrap",
                )}
                onClick={() => handleStockTypeChange(option.value)}
              >
                <NextImage
                  src={option.iconSrc}
                  alt={option.label}
                  width={16}
                  height={16}
                  className="w-4 h-4 shrink-0"
                />
                <span className="body_medium_m text-content_primary">
                  {option.label}
                </span>
                {stockType === option.value && (
                  <OspIcons
                    name="check"
                    size={20}
                    color={ospThemes.colors.content_brand_primary}
                    className="shrink-0"
                  />
                )}
              </div>
            ))}
          </div>
        )}
        {/* 按钮本身 */}
        <div
          data-openreplay-label="Home · StockMarketSwitch · div"
          className={cn(
            "flex items-center md:gap-0.5 gap-1 cursor-pointer rounded-[6px] md:py-[7px] py-1 md:px-[11px] px-2 w-fit",
            "body_medium_s text-content_primary",
            "md:border-1 border-0 border-border_white_transparent10 ",
            "md:bg-transparent bg-background_white_overlay10",
            isOpen &&
              "md:bg-background_brand_overlay5 bg-background_white_overlay10 border-border_brand_transparent50",
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <NextImage
            src={currentOption.iconSrc}
            alt={currentOption.label}
            width={isPc ? 20 : 12}
            height={isPc ? 20 : 12}
            className="md:w-5 md:h-5 w-3 h-3 shrink-0"
          />
          <span className="text-content_primary md:body_medium_m botton_s">
            {currentOption.label}
          </span>
          <OspIcons
            name="chevron-up"
            size={isPc ? 20 : 16}
            color={ospThemes.colors.content_secondary}
            className={cn(
              "transition-transform duration-100 shrink-0",
              isOpen ? "rotate-0" : "rotate-180",
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const RatingStockMarketSwitch = ({
  hidden: defaultHidden,
}: {
  hidden?: boolean;
}) => {
  const pathname = usePathname();
  const hidden = (() => {
    if (pathname) {
      return pathname.includes("crypto");
    }
    return defaultHidden;
  })();

  return (
    <StockMarketSwitch
      align="right"
      className={cn("ml-auto", { hidden: hidden })}
    />
  );
};
